import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import {
  IMPORT_STATE,
  type ImportHistoryItemT,
  type KustomerInfo,
} from '../types';

const mapKustomerInfo = (data: unknown): KustomerInfo | null => {
  if (!data || typeof data !== 'object') return null;

  const info = data as {
    workspace_id?: string;
    alias?: string;
    domain?: string;
    state?: IMPORT_STATE;
    import_state?: IMPORT_STATE;
    history?: ImportHistoryItemT[];
  };

  if (!info.alias) return null;

  return {
    workspace_id: info.workspace_id || info.alias,
    alias: info.alias,
    domain: info.domain || '',
    import_state: info.import_state || info.state || IMPORT_STATE.NOT_STARTED,
    history: info.history || [],
  };
};

export const useKustomer = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;

  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKey = getIntegrationQueryKeys(
    SOURCE_TYPE.KUSTOMER,
    workspaceAlias,
  );

  const { data: kustomerWorkspace } = useQuery({
    queryKey,
    queryFn: async () =>
      mapKustomerInfo(await service.kustomer.getKustomerInfo(workspaceAlias)),
    enabled,
  });

  const kustomerAlias = kustomerWorkspace?.alias ?? '';

  const { mutate: disconnectKustomerMutation } = useMutation({
    mutationFn: () => service.kustomer.deleteKustomerInfo(kustomerAlias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: ({ start_date, end_date }: NullableDateRangeT) => {
      if (!start_date || !end_date) return Promise.resolve(false);
      const startDate: string = start_date;
      const endDate: string = end_date;
      return service.kustomer.importWorkspaceFeedback(
        kustomerAlias,
        startDate,
        endDate,
      );
    },
    onSuccess: () => {
      queryClient.setQueryData<KustomerInfo | null>(queryKey, (current) => {
        if (!current) return current;

        return {
          ...current,
          import_state: IMPORT_STATE.IMPORTING,
        };
      });
    },
  });

  return {
    kustomerWorkspace,
    disconnectKustomerMutation,
    requestImportMutation,
    setKustomerWorkspace: (workspace: KustomerInfo) => {
      queryClient.setQueryData<KustomerInfo | null>(queryKey, workspace);
    },
    invalidateKustomerQuery: () => queryClient.invalidateQueries({ queryKey }),
  };
};
